import React, { useState, useEffect } from 'react';
import { Plus, Filter, Search, RefreshCw, MessageCircle } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useSystemPreset } from '../../contexts/SystemPresetContext';
import { CommunityService } from '../../services/communityService';
import ForumCard from './ForumCard';
import CreateForumModal from './CreateForumModal';
import ForumDetailsPanel from './ForumDetailsPanel';
import { SectionHeader } from '../ui';
import type { CommunityForum, ForumCategory } from '../../types/community';

interface ForumsSectionProps {
  searchQuery: string;
}

const ForumsSection: React.FC<ForumsSectionProps> = ({ searchQuery }) => {
  const { language, isRTL } = useLocalization();
  const { presetData } = useSystemPreset();
  const [forums, setForums] = useState<CommunityForum[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedForum, setSelectedForum] = useState<CommunityForum | null>(null);

  useEffect(() => {
    loadForums();
  }, []);

  const loadForums = async () => {
    try {
      setLoading(true);
      console.log('🔄 ForumsSection - Loading forums...');
      const forumsData = await CommunityService.getForums();
      console.log('✅ ForumsSection - Forums loaded:', forumsData.length, 'forums');
      console.log('📋 ForumsSection - Forum details:', forumsData.map(f => ({
        id: f.id,
        title: f.title,
        isActive: f.isActive,
        isDeleted: f.isDeleted,
        categoryId: f.categoryId,
        createdAt: f.createdAt
      })));
      setForums(forumsData);
    } catch (error) {
      console.error('❌ ForumsSection - Error loading forums:', error);
    } finally {
      setLoading(false);
    }
  };

  // Transform SystemPresetData forum categories to the format expected by the component
  const categories: ForumCategory[] = presetData?.forumCategories
    ?.map(category => ({
      id: category.id,
      name: category.name_en,
      nameAr: category.name_ar,
      description: category.description,
      descriptionAr: category.descriptionAr,
      icon: category.icon,
      color: category.color,
      order: category.order,
      isActive: category.isActive
    }))
    ?.sort((a, b) => a.order - b.order) || []; // Sort by order

  // Debug logging
  console.log('🔍 ForumsSection - Categories loaded:', categories.length);
  console.log('🔍 ForumsSection - Preset data available:', !!presetData);
  console.log('🔍 ForumsSection - Forum categories from preset:', presetData?.forumCategories?.length || 0);
  console.log('🔍 ForumsSection - Raw preset categories:', presetData?.forumCategories);
  console.log('🔍 ForumsSection - Categories for dropdown:', categories.map(c => ({ id: c.id, name: c.name, nameAr: c.nameAr, isActive: c.isActive })));
  console.log('🔍 ForumsSection - Current language:', language);

  const filteredForums = forums.filter(forum => {
    const matchesSearch = searchQuery === '' || 
      forum.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      forum.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || 
      forum.categoryId === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleForumClick = (forum: CommunityForum) => {
    setSelectedForum(forum);
    console.log('Selected forum:', forum.id, forum.title);
  };

  const handleCreateForum = () => {
    setShowCreateModal(true);
  };

  const handleForumCreated = () => {
    console.log('🎉 ForumsSection - Forum created successfully, reloading forums...');
    loadForums(); // Reload forums after creation
  };

  const handleRefresh = () => {
    loadForums();
  };

  const handleAddForum = () => {
    setShowCreateModal(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 animate-pulse">
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded mb-3"></div>
              <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded mb-2"></div>
              <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <SectionHeader
          title={language === 'ar' ? 'المنتديات' : 'Forums'}
          description={language === 'ar' ? 'شارك في المناقشات وتبادل الخبرات مع المحامين الآخرين' : 'Join discussions and share experiences with other lawyers'}
          language={language}
          isRTL={isRTL}
          refreshButton={{
            onClick: handleRefresh,
            disabled: loading,
            loading: loading
          }}
          addButton={{
            label: language === 'ar' ? 'إنشاء منتدى' : 'Create Forum',
            onClick: handleAddForum
          }}
        >
          {/* Category Filter */}
          <div className="relative">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`${isRTL ? 'pr-4 pl-4' : 'pl-4 pr-4'} py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent min-w-[150px] ${
                isRTL ? 'text-right' : 'text-left'
              }`}
              style={{
                textAlign: isRTL ? 'right' : 'left'
              }}
            >
              <option value="all">
                {language === 'ar' ? 'جميع الفئات' : 'All Categories'}
              </option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {language === 'ar' ? category.nameAr : category.name}
                </option>
              ))}
            </select>
          </div>
        </SectionHeader>

      {/* Two Panel Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Forums List */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                {language === 'ar' ? 'المنتديات' : 'Forums'}
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                {filteredForums.length} {language === 'ar' ? 'منتدى' : 'forum'}{filteredForums.length !== 1 ? (language === 'ar' ? 'ات' : 's') : ''} {language === 'ar' ? 'موجود' : 'found'}
              </p>
            </div>
            
            <div className="p-4">
              {loading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, index) => (
                    <div key={index} className="bg-slate-100 dark:bg-slate-700 rounded-xl p-4 animate-pulse">
                      <div className="h-4 bg-slate-200 dark:bg-slate-600 rounded mb-2"></div>
                      <div className="h-3 bg-slate-200 dark:bg-slate-600 rounded w-2/3"></div>
                    </div>
                  ))}
                </div>
              ) : filteredForums.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Search className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    {language === 'ar' ? 'لا توجد منتديات' : 'No forums found'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredForums.map((forum) => {
                    const isSelected = selectedForum?.id === forum.id;
                    
                    return (
                      <div
                        key={forum.id}
                        className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                          isSelected 
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-primary shadow-sm' 
                            : 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                        }`}
                        onClick={() => handleForumClick(forum)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary to-blue-700 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                              <MessageCircle className="w-5 h-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="font-semibold text-slate-800 dark:text-white text-sm truncate">
                                {forum.title}
                              </h3>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {forum.threadCount} {language === 'ar' ? 'مناقشة' : 'threads'}
                              </p>
                            </div>
                          </div>
                          <div className="text-xs text-slate-400">
                            {new Date(forum.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Forum Details */}
        <div className="lg:col-span-2">
          <ForumDetailsPanel
            forum={selectedForum}
            onClose={() => setSelectedForum(null)}
          />
        </div>
      </div>
      </div>

      {/* Create Forum Modal */}
      <CreateForumModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleForumCreated}
      />
    </div>
  );
};

export default ForumsSection;
